import React, { useState, useEffect } from 'react';
import { FiX, FiUpload, FiCheck, FiAlertCircle, FiLoader, FiShield } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const KYCVerificationModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    nationality: '',
    phone_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
    id_type: 'passport',
    id_number: ''
  });
  const [files, setFiles] = useState({
    id_document: null,
    selfie: null,
    proof_of_address: null
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchExistingSubmission();
    }
  }, [isOpen, user]);

  const fetchExistingSubmission = async () => {
    try {
      const { data, error } = await supabase
        .from('kyc_submissions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (data && !error) {
        setExistingSubmission(data);
      }
    } catch (err) {
      // No existing submission
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      setFiles({ ...files, [field]: file });
    }
  };

  const uploadFile = async (file, folder) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${folder}/${Date.now()}.${fileExt}`;
    
    const { error } = await supabase.storage
      .from('kyc-documents')
      .upload(fileName, file);
    
    if (error) throw error;
    
    // Store the storage path (not public URL) so we can use signed URLs in admin
    return fileName;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Upload files
      let idDocUrl = null;
      let selfieUrl = null;
      let proofUrl = null;

      if (files.id_document) {
        idDocUrl = await uploadFile(files.id_document, 'id-document');
      }
      if (files.selfie) {
        selfieUrl = await uploadFile(files.selfie, 'selfie');
      }
      if (files.proof_of_address) {
        proofUrl = await uploadFile(files.proof_of_address, 'proof-of-address');
      }

      // Create submission
      const { error: insertError } = await supabase
        .from('kyc_submissions')
        .insert({
          user_id: user.id,
          user_email: user.email,
          ...formData,
          id_document_url: idDocUrl,
          selfie_url: selfieUrl,
          proof_of_address_url: proofUrl,
          status: 'pending'
        });

      if (insertError) throw insertError;

      // Update user profile
      await supabase
        .from('user_profiles')
        .update({ kyc_status: 'pending' })
        .eq('user_id', user.id);

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setStep(1);
      }, 2000);

    } catch (err) {
      setError(err.message || 'Failed to submit KYC');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      under_review: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const labels = {
      pending: 'Pending Review',
      under_review: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
                <FiShield className="text-cyan-400" size={20} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Identity Verification</h2>
                <p className="text-sm text-gray-400">Optional KYC verification</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <FiX size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[70vh]">
            {success ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <FiCheck className="text-green-400" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Submission Received</h3>
                <p className="text-gray-400">Your KYC documents have been submitted for review.</p>
              </div>
            ) : existingSubmission ? (
              <div className="space-y-6">
                <div className="bg-gray-800/50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-white">Your Verification Status</h3>
                    {getStatusBadge(existingSubmission.status)}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Full Name:</span>
                      <p className="text-white">{existingSubmission.full_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">ID Type:</span>
                      <p className="text-white capitalize">{existingSubmission.id_type?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Submitted:</span>
                      <p className="text-white">{new Date(existingSubmission.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Country:</span>
                      <p className="text-white">{existingSubmission.country || 'Not provided'}</p>
                    </div>
                  </div>

                  {existingSubmission.status === 'rejected' && existingSubmission.rejection_reason && (
                    <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm">
                        <strong>Rejection Reason:</strong> {existingSubmission.rejection_reason}
                      </p>
                    </div>
                  )}

                  {existingSubmission.status === 'rejected' && (
                    <button
                      onClick={() => setExistingSubmission(null)}
                      className="mt-4 w-full py-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 transition-all font-medium shadow-lg shadow-purple-500/20"
                    >
                      Submit New Verification
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Progress */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        s === step ? 'bg-purple-500' : s < step ? 'bg-green-500' : 'bg-gray-700'
                      }`}
                    />
                  ))}
                </div>

                {step === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-white mb-4">Personal Information</h3>
                    
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Full Legal Name *</label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        placeholder="As shown on your ID"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Date of Birth</label>
                        <input
                          type="date"
                          name="date_of_birth"
                          value={formData.date_of_birth}
                          onChange={handleInputChange}
                          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Nationality</label>
                        <input
                          type="text"
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleInputChange}
                          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                          placeholder="e.g., United States"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-white mb-4">Address Information</h3>
                    
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Address Line 1</label>
                      <input
                        type="text"
                        name="address_line1"
                        value={formData.address_line1}
                        onChange={handleInputChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        placeholder="Street address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Address Line 2</label>
                      <input
                        type="text"
                        name="address_line2"
                        value={formData.address_line2}
                        onChange={handleInputChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        placeholder="Apt, suite, etc. (optional)"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">State/Province</label>
                        <input
                          type="text"
                          name="state_province"
                          value={formData.state_province}
                          onChange={handleInputChange}
                          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Postal Code</label>
                        <input
                          type="text"
                          name="postal_code"
                          value={formData.postal_code}
                          onChange={handleInputChange}
                          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Country</label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-white mb-4">Identity Documents</h3>
                    
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">ID Type</label>
                      <select
                        name="id_type"
                        value={formData.id_type}
                        onChange={handleInputChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                      >
                        <option value="passport">Passport</option>
                        <option value="national_id">National ID Card</option>
                        <option value="drivers_license">Driver's License</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">ID Number</label>
                      <input
                        type="text"
                        name="id_number"
                        value={formData.id_number}
                        onChange={handleInputChange}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                        placeholder="Document number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        Upload ID Document
                        <span className="text-gray-500 ml-2">(Optional)</span>
                      </label>
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-700 rounded-xl cursor-pointer hover:border-purple-500 transition-colors">
                        {files.id_document ? (
                          <div className="flex items-center gap-2 text-green-400">
                            <FiCheck size={20} />
                            <span>{files.id_document.name}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center text-gray-400">
                            <FiUpload size={24} className="mb-2" />
                            <span>Click to upload ID document</span>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(e, 'id_document')}
                          className="hidden"
                        />
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        Upload Selfie with ID
                        <span className="text-gray-500 ml-2">(Optional)</span>
                      </label>
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-700 rounded-xl cursor-pointer hover:border-purple-500 transition-colors">
                        {files.selfie ? (
                          <div className="flex items-center gap-2 text-green-400">
                            <FiCheck size={20} />
                            <span>{files.selfie.name}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center text-gray-400">
                            <FiUpload size={24} className="mb-2" />
                            <span>Click to upload selfie</span>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'selfie')}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
                    <FiAlertCircle size={20} />
                    <span>{error}</span>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex gap-4 pt-4">
                  {step > 1 && (
                    <button
                      onClick={() => setStep(step - 1)}
                      className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/30 hover:from-purple-600/20 hover:to-cyan-600/20 hover:border-purple-500/50 text-cyan-400 transition-all font-medium"
                    >
                      Back
                    </button>
                  )}
                  
                  {step < 3 ? (
                    <button
                      onClick={() => setStep(step + 1)}
                      disabled={step === 1 && !formData.full_name}
                      className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 transition-all font-medium shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:shadow-none"
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={loading || !formData.full_name}
                      className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 transition-all font-medium shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:shadow-none flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <FiLoader className="animate-spin" size={20} />
                          Submitting...
                        </>
                      ) : (
                        'Submit Verification'
                      )}
                    </button>
                  )}
                </div>

                <p className="text-center text-sm text-gray-500">
                  This verification is optional and helps build trust with our platform.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default KYCVerificationModal;
